/**
 * Écran « Démarrage interrompu ».
 *
 * Il doit toujours proposer une VRAIE issue :
 *  - un code d'erreur stable (NX-…) à communiquer au support, jamais de message technique brut ;
 *  - « Réessayer » qui relance réellement l'ouverture (côté Electron, l'échec d'ouverture mis en cache par
 *    le processus principal est d'abord oublié) ;
 *  - « Restaurer la dernière sauvegarde » quand une copie automatique existe ;
 *  - « Ouvrir le dossier des journaux » et « Contacter Nexxus » (code et version joints).
 *
 * Aucune action en ligne dans le HTML : écouteurs posés ici (compatible CSP stricte).
 */

export type BootFailureCode = 'NX-BOOT-001' | 'NX-BOOT-002';

const NETWORK_OR_TIMEOUT = /timeout|délai|bloqué|étape|fetch|network|Failed to fetch|injoignable/i;

/** Classe une erreur d'interface : réseau / délai (BOOT-002) ou autre (BOOT-001). */
export function classifyBootError(err: unknown): { code: BootFailureCode; technical: string } {
  const technical = err instanceof Error ? err.message : String(err);
  return { code: NETWORK_OR_TIMEOUT.test(technical) ? 'NX-BOOT-002' : 'NX-BOOT-001', technical };
}

export interface StartupFailureView {
  code: string;
  message: string;
  canRestore: boolean;
  canOpenLogs: boolean;
  canContact: boolean;
}

const MESSAGE_BOOT_002 =
  'L’ouverture de Nexxus n’a pas pu se terminer. Vérifiez votre connexion Internet, puis réessayez. Vos données ne sont pas perdues.';
const MESSAGE_BOOT_001 =
  'L’ouverture de Nexxus a rencontré un problème. Vos données n’ont pas été supprimées. Essayez de réessayer ; si le message revient, contactez Nexxus en indiquant le code ci-dessous.';

/** Actions de reprise réellement offertes par l'hôte (absentes dans le navigateur). */
export interface StartupBridgeCapabilities {
  restoreLastBackup?: unknown;
  openLogsFolder?: unknown;
}

/** Texte et actions à afficher, à partir du diagnostic du processus principal (absent dans le navigateur). */
export function buildStartupFailureView(
  boot: { code: BootFailureCode },
  report: ElectronStartupReport | null,
  bridge: StartupBridgeCapabilities,
): StartupFailureView {
  const dbCode = report && !report.dbOk ? report.code : null;
  return {
    code: dbCode ?? boot.code,
    message:
      dbCode && report?.message
        ? report.message
        : boot.code === 'NX-BOOT-002'
          ? MESSAGE_BOOT_002
          : MESSAGE_BOOT_001,
    canRestore: Boolean(report?.lastBackup && bridge.restoreLastBackup),
    canOpenLogs: Boolean(bridge.openLogsFolder),
    canContact: true,
  };
}

const SUPPORT_EMAIL_FALLBACK = 'contact@nexxus.ga';

function button(action: string, label: string, primary: boolean): string {
  const style = primary
    ? 'padding:8px 16px;background:var(--teal);color:white;border:none;border-radius:4px;cursor:pointer;'
    : 'padding:8px 16px;background:transparent;color:var(--marine);border:1px solid var(--teal);border-radius:4px;cursor:pointer;';
  return `<button type="button" data-startup-action="${action}" style="${style}">${label}</button>`;
}

/** Affiche l'écran puis câble les actions. Ne lève jamais (dernier filet de l'application). */
export async function renderStartupFailureScreen(root: HTMLElement, err: unknown): Promise<void> {
  const boot = classifyBootError(err);
  const bridge = window.electron;
  let report: ElectronStartupReport | null = null;
  try {
    report = (await bridge?.getStartupReport?.()) ?? null;
  } catch {
    report = null;
  }
  const view = buildStartupFailureView(boot, report, bridge ?? {});
  // Journal de démarrage : cause exacte (nettoyée côté principal), pour le support.
  void Promise.resolve(bridge?.logStartupFailure?.(view.code, boot.technical)).catch(() => undefined);

  root.className = '';
  root.innerHTML = `
    <div class="login-page">
      <div class="login-card" role="alert">
        <h2 style="margin-top:0;color:var(--marine);">Démarrage interrompu</h2>
        <p id="startup-message" style="color:var(--gris);"></p>
        <p style="color:var(--gris);">Code d’erreur : <strong id="startup-code"></strong></p>
        <div id="startup-feedback" role="status" style="color:var(--gris);min-height:1.2em;"></div>
        <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:12px;">
          ${button('retry', 'Réessayer', true)}
          ${view.canRestore ? button('restore', 'Restaurer la dernière sauvegarde', false) : ''}
          ${view.canOpenLogs ? button('logs', 'Ouvrir le dossier des journaux', false) : ''}
          ${button('contact', 'Contacter Nexxus', false)}
        </div>
      </div>
    </div>`;
  // Textes injectés en textContent : jamais de HTML issu d'une erreur.
  const set = (id: string, text: string): void => {
    const el = root.querySelector(`#${id}`);
    if (el) el.textContent = text;
  };
  set('startup-message', view.message);
  set('startup-code', view.code);
  const feedback = (text: string): void => set('startup-feedback', text);

  const actions: Record<string, () => Promise<void>> = {
    retry: async () => {
      feedback('Nouvel essai en cours…');
      try {
        await bridge?.retryStartup?.();
      } finally {
        window.location.reload();
      }
    },
    restore: async () => {
      const result = await bridge?.restoreLastBackup?.();
      if (result?.ok) {
        feedback('Sauvegarde restaurée. Redémarrage…');
        window.location.reload();
      } else if (!result?.canceled) {
        feedback(`La restauration n’a pas pu être faite${result?.error ? ` : ${result.error}` : '.'}`);
      }
    },
    logs: async () => {
      const result = await bridge?.openLogsFolder?.();
      if (!result?.ok) feedback('Impossible d’ouvrir le dossier des journaux.');
    },
    contact: async () => {
      try {
        const result = await bridge?.contactSupport?.(view.code);
        feedback(`Écrivez à ${result?.email ?? SUPPORT_EMAIL_FALLBACK} en indiquant le code ${view.code}.`);
      } catch {
        feedback(`Écrivez à ${SUPPORT_EMAIL_FALLBACK} en indiquant le code ${view.code}.`);
      }
    },
  };
  for (const el of Array.from(root.querySelectorAll<HTMLButtonElement>('[data-startup-action]'))) {
    el.addEventListener('click', () => {
      const run = actions[el.dataset.startupAction ?? ''];
      if (run) void run().catch(() => feedback('Action impossible pour le moment.'));
    });
  }
}
